import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Alert, AlertDescription } from './ui/alert';
import { FileText, Upload, Sparkles, Loader2, CheckCircle2, AlertCircle } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Checkbox } from './ui/checkbox';
import { projectId } from '../utils/supabase/info';
import { getAuthHeaders, getAuthHeadersWithJson } from '../utils/apiHeaders';
import { toast } from 'sonner';
import { normalizeAcronyms } from '../utils/normalizeAcronyms';

interface AddSOWDialogProps {
  open: boolean;
  onClose: () => void;
  accessToken: string;
  consultingFirmId: string;
  preselectedCompanyId?: string;
  /**
   * When the dialog is opened from a specific client context (e.g. ClientDetailView),
   * pass the company's display name alongside `preselectedCompanyId`. In that case the
   * dialog hides the client-company dropdown and renders the name read-only. Without
   * this, Radix Select can't resolve the preselected id to a label if the companies
   * list hasn't loaded, producing an empty-looking "required" field.
   */
  preselectedCompanyName?: string;
  /**
   * Called on every success branch (save, analyze, apply suggestions). The
   * argument names the engagement just created so parents can offer the
   * scope-reconciliation step (spec §4.10) when the company already had one.
   */
  onSOWCreated?: (created?: SOWCreatedInfo) => void;
}

export interface SOWCreatedInfo {
  engagementId: string;
  companyId: string;
}

interface Company {
  companyId: string;
  companyName: string;
}

interface NewConfigItem {
  name: string;
  category: string;
  description: string;
  phase: number;
  reason: string;
  coreFeature: boolean;
  estimatedHours: number;
}

interface ExistingItemSuggestion {
  itemId: string;
  enabled: boolean;
  phase: number;
  reason: string;
}

interface ConfigItem {
  id: string;
  name: string;
  category: string;
  description: string;
  defaultPhase: number;
  coreFeature: boolean;
}

export function AddSOWDialog({ open, onClose, accessToken, consultingFirmId, preselectedCompanyId, preselectedCompanyName, onSOWCreated }: AddSOWDialogProps) {
  const isClientContext = Boolean(preselectedCompanyId && preselectedCompanyName);
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  // The SOW itself is uploaded as a file (PDF/DOCX/TXT) -- pasted text is no
  // longer supported. By the time a consultant adds an SOW it should be a
  // signed, executed contract document, so a file is the correct input.
  // Sales discovery notes are captured separately so the LLM can use them
  // as supplementary context (HLOs the BDM heard from the client) without
  // mixing them into the binding scope.
  const [sowFile, setSowFile] = useState<File | null>(null);
  // The engagement minted in this dialog session, reported on every success branch.
  const [createdEngagementId, setCreatedEngagementId] = useState<string | null>(null);
  const [salesNotes, setSalesNotes] = useState('');
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzed, setAnalyzed] = useState(false);
  const [newItemSuggestions, setNewItemSuggestions] = useState<NewConfigItem[]>([]);
  const [existingItemSuggestions, setExistingItemSuggestions] = useState<ExistingItemSuggestion[]>([]);
  const [selectedNewItems, setSelectedNewItems] = useState<number[]>([]);
  const [selectedExistingItems, setSelectedExistingItems] = useState<number[]>([]);
  const [configDefaults, setConfigDefaults] = useState<ConfigItem[]>([]);
  // Company-level AI preference gates the primary footer action. Defaults to
  // true to match the server-side default in GET /companies/:id/preferences
  // and to avoid a flicker from "Save SOW" → "Analyze & Save" when the
  // preference arrives. Flips to false only when the preference explicitly
  // says aiEnabled === false.
  const [companyAiEnabled, setCompanyAiEnabled] = useState<boolean>(true);

  useEffect(() => {
    if (open) {
      if (!isClientContext) {
        loadCompanies();
      }
      loadConfigDefaults();
      // Reset state
      setSelectedCompanyId(preselectedCompanyId || '');
      setSowFile(null);
      setSalesNotes('');
      setAnalyzed(false);
      setNewItemSuggestions([]);
      setExistingItemSuggestions([]);
      setSelectedNewItems([]);
      setSelectedExistingItems([]);
    }
  }, [open, preselectedCompanyId]);

  // Load the company's AI preference. We optimistically assume enabled and
  // only flip to disabled when the server says so, so the footer button
  // never flickers from "Save SOW" to "Analyze & Save" during load.
  useEffect(() => {
    if (!selectedCompanyId) {
      setCompanyAiEnabled(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/companies/${selectedCompanyId}/preferences`,
          { headers: getAuthHeaders(accessToken) },
        );
        if (!response.ok) {
          if (!cancelled) setCompanyAiEnabled(true);
          return;
        }
        const data = await response.json();
        const prefs = data?.preferences || {};
        // Treat only explicit `false` as disabled; everything else (missing,
        // true, malformed) stays on the default AI path.
        if (!cancelled) {
          setCompanyAiEnabled(prefs.aiEnabled === false ? false : true);
        }
      } catch (err) {
        if (!cancelled) setCompanyAiEnabled(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedCompanyId, accessToken]);

  const loadConfigDefaults = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/defaults`,
        {
          headers: getAuthHeaders(accessToken),
        }
      );

      if (response.ok) {
        const data = await response.json();
        setConfigDefaults(data.items || []);
      }
    } catch (error) {
      console.error('Error loading config defaults:', error);
    }
  };

  const loadCompanies = async () => {
    try {
      const response = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/consultant/companies`,
        {
          headers: getAuthHeaders(accessToken),
        }
      );

      if (response.ok) {
        const data = await response.json();
        console.log('[AddSOWDialog] Loaded companies:', data);
        setCompanies(
          (data || []).map((company: any) => ({
            companyId: company.id,
            companyName: company.name,
          }))
        );
      }
    } catch (error) {
      console.error('Error loading companies:', error);
    }
  };

  /**
   * Validate that the input the user supplied is something we can actually
   * upload. We accept PDF, DOCX, and TXT -- the same set the backend's
   * extractText() helper handles. Reject anything else up-front so the user
   * gets a fast, clear error instead of a 400 from the upload route.
   */
  const isAcceptedSowFile = (file: File): boolean => {
    const name = (file.name || '').toLowerCase();
    return (
      name.endsWith('.pdf') ||
      name.endsWith('.docx') ||
      name.endsWith('.txt') ||
      file.type === 'application/pdf' ||
      file.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
      file.type === 'text/plain'
    );
  };

  /**
   * Common preflight for both Save and Analyze paths. Emits a toast and
   * returns false on failure so the caller can early-return.
   */
  const preflightInputs = (): boolean => {
    if (!sowFile) {
      toast.error('Please upload the executed SOW file (PDF, DOCX, or TXT)');
      return false;
    }
    if (!isAcceptedSowFile(sowFile)) {
      toast.error('Unsupported file type. Upload a PDF, DOCX, or TXT.');
      return false;
    }
    if (!selectedCompanyId) {
      toast.error('Please select a client company');
      return false;
    }
    return true;
  };

  /**
   * Mint a new engagement via the canonical POST /sows handler. Returns the
   * server-assigned UUID id. scopeDescription is intentionally NOT sent --
   * the upload-sow call that follows extracts the SOW text and writes it
   * back into the engagement, keeping a single source of truth for scope.
   */
  const createCanonicalEngagement = async (): Promise<string> => {
    const createResponse = await fetch(
      `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/sows`,
      {
        method: 'POST',
        headers: getAuthHeadersWithJson(accessToken),
        body: JSON.stringify({
          companyId: selectedCompanyId,
          sow: {
            sowId: `SOW-${new Date().getFullYear()}-${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`,
            status: 'active',
            changeOrders: [],
            sowFileName: sowFile?.name,
          },
        }),
      },
    );

    if (!createResponse.ok) {
      let serverDetail = '';
      try {
        const body = await createResponse.json();
        serverDetail = body?.userMessage || body?.detail || body?.error || '';
        console.error('[AddSOWDialog] /sows create failed:', createResponse.status, body);
      } catch {
        serverDetail = (await createResponse.text().catch(() => '')).slice(0, 200);
      }
      throw new Error(
        `Failed to save SOW (${createResponse.status}${serverDetail ? `: ${serverDetail}` : ''})`,
      );
    }

    const created = await createResponse.json().catch(() => ({}));
    const newSowId: string | undefined = created?.id || created?.sow?.id;
    if (!newSowId) {
      console.error('[AddSOWDialog] /sows create returned unexpected shape:', created);
      throw new Error('SOW created but server response missing id');
    }
    setCreatedEngagementId(newSowId);
    return newSowId;
  };

  /**
   * Stream the uploaded SOW file (and optional sales notes) into the
   * upload-sow pipeline. The backend extracts text, persists it on the
   * engagement, and -- unless skipAi=true -- runs HLO extraction. Failures
   * here are surfaced as warnings (engagement already exists) so the user
   * can re-upload from the SOW card without losing the SOW record itself.
   */
  const uploadSowFileToEngagement = async (
    engagementId: string,
    options: { skipAi?: boolean } = {},
  ): Promise<{ hlosExtracted: number } | null> => {
    if (!sowFile) return null;

    const formData = new FormData();
    formData.append('file', sowFile);
    if (salesNotes.trim()) {
      formData.append('salesNotes', salesNotes.trim());
    }

    const url = options.skipAi
      ? `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagements/${engagementId}/upload-sow?skipAi=true`
      : `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagements/${engagementId}/upload-sow`;

    const uploadResponse = await fetch(url, {
      method: 'POST',
      headers: { Authorization: `Bearer ${accessToken}` },
      body: formData,
    });

    if (!uploadResponse.ok) {
      let serverDetail = '';
      try {
        const body = await uploadResponse.json();
        serverDetail = body?.error || body?.detail || '';
      } catch {
        serverDetail = (await uploadResponse.text().catch(() => '')).slice(0, 200);
      }
      console.warn(
        `[AddSOWDialog] /engagements/${engagementId}/upload-sow failed (${uploadResponse.status}):`,
        serverDetail,
      );
      toast.warning('SOW saved but file upload failed', {
        description:
          serverDetail ||
          'Re-upload the SOW file from the engagement page to enable AI analysis.',
      });
      return null;
    }

    const uploadData = await uploadResponse.json().catch(() => ({}));
    const hlosExtracted = uploadData?.ai?.hlosExtracted ?? 0;
    console.log(
      `[AddSOWDialog] upload-sow success: ${hlosExtracted} HLOs extracted for engagement ${engagementId}`,
    );
    return { hlosExtracted };
  };

  const handleAnalyzeSOW = async () => {
    if (!preflightInputs()) return;

    setAnalyzing(true);
    try {
      const newSowId = await createCanonicalEngagement();

      // Notify parent immediately so the SOW card appears in the list
      // before HLO extraction or config analysis finishes.
      if (onSOWCreated) onSOWCreated({ engagementId: newSowId, companyId: selectedCompanyId });

      // Pipeline step 1: extract text + HLOs from the uploaded file. This
      // also updates engagement.scopeDescription so step 2 has scope text
      // to analyze. Failures here are non-fatal -- engagement still exists
      // and config analysis can continue against any prior scope context.
      await uploadSowFileToEngagement(newSowId);

      // Pipeline step 2: ask the analyzer for config-item suggestions.
      const analyzePayload: any = {
        sowIds: [newSowId],
      };

      const analyzeResponse = await fetch(
        `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/analyze-sow/${selectedCompanyId}`,
        {
          method: 'POST',
          headers: getAuthHeadersWithJson(accessToken),
          body: JSON.stringify(analyzePayload),
        }
      );

      if (!analyzeResponse.ok) {
        let error: any = {};
        try {
          error = await analyzeResponse.json();
        } catch {
          error = { error: await analyzeResponse.text().catch(() => '') };
        }
        console.error(
          '[AddSOWDialog] /config/analyze-sow failed:',
          analyzeResponse.status,
          error,
        );
        if (error.isQuotaError) {
          toast.warning('AI analysis unavailable', {
            description: 'SOW saved successfully, but AI analysis is temporarily unavailable. Configure manually.',
          });
          setAnalyzed(true);
          return;
        }
        throw new Error(
          error.userMessage || error.error || `Failed to analyze SOW (${analyzeResponse.status})`,
        );
      }

      const analysisData = await analyzeResponse.json();
      // Keep a lightweight trace of the shape we actually received so that a
      // backend returning an unexpected payload (e.g. `{ success: true }` with
      // no arrays) never again produces a silently empty suggestion panel.
      console.log('[AddSOWDialog] analyze-sow response shape:', {
        newItemSuggestions: Array.isArray(analysisData?.newItemSuggestions)
          ? analysisData.newItemSuggestions.length
          : typeof analysisData?.newItemSuggestions,
        existingItemSuggestions: Array.isArray(analysisData?.existingItemSuggestions)
          ? analysisData.existingItemSuggestions.length
          : typeof analysisData?.existingItemSuggestions,
        keys: analysisData ? Object.keys(analysisData) : null,
      });
      setNewItemSuggestions(analysisData.newItemSuggestions || []);
      setExistingItemSuggestions(analysisData.existingItemSuggestions || []);
      
      // Select all items by default
      setSelectedNewItems((analysisData.newItemSuggestions || []).map((_: any, index: number) => index));
      setSelectedExistingItems(
        (analysisData.existingItemSuggestions || [])
          .map((item: ExistingItemSuggestion, index: number) => (item.enabled ? index : -1))
          .filter((index: number) => index !== -1)
      );
      
      setAnalyzed(true);

      toast.success('SOW added and analyzed');
    } catch (error: any) {
      console.error('Error analyzing SOW:', error);
      toast.error(error.message || 'Failed to analyze SOW');
    } finally {
      setAnalyzing(false);
    }
  };

  /**
   * Save without invoking the LLM analyzer. Still uploads the file so the
   * SOW document is persisted alongside the engagement record -- skipAi=true
   * tells the upload route to skip OpenAI extraction. The user can always
   * trigger Analyze later from the SOW card.
   */
  const handleSaveSOW = async () => {
    if (!preflightInputs()) return;

    setSaving(true);
    try {
      const newSowId = await createCanonicalEngagement();

      // Upload the file so the SOW document lives next to the engagement.
      // skipAi=true keeps us from burning OpenAI tokens when the company has
      // AI disabled. HLOs can be extracted later via Re-analyze on the card.
      await uploadSowFileToEngagement(newSowId, { skipAi: true });

      toast.success('SOW saved');

      if (onSOWCreated) onSOWCreated({ engagementId: newSowId, companyId: selectedCompanyId });
      onClose();
    } catch (error: any) {
      console.error('Error saving SOW:', error);
      toast.error(error.message || 'Failed to save SOW');
    } finally {
      setSaving(false);
    }
  };

  const handleApplySuggestions = async () => {
    setLoading(true);
    try {
      // Apply only selected existing item suggestions
      const enabledExistingItems = existingItemSuggestions
        .filter((_, index) => selectedExistingItems.includes(index));
      
      for (const suggestion of enabledExistingItems) {
        await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/company/${selectedCompanyId}/override`,
          {
            method: 'POST',
            headers: getAuthHeadersWithJson(accessToken),
            body: JSON.stringify({
              override: {
                configItemId: suggestion.itemId,
                phase: suggestion.phase,
                enabled: suggestion.enabled,
              },
            }),
          }
        );
      }

      // Create only selected new configuration items
      const selectedNewItemsList = newItemSuggestions.filter((_, index) => 
        selectedNewItems.includes(index)
      );
      
      for (const newItem of selectedNewItemsList) {
        const newItemId = `custom-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
        
        await fetch(
          `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/config/company/${selectedCompanyId}/override`,
          {
            method: 'POST',
            headers: getAuthHeadersWithJson(accessToken),
            body: JSON.stringify({
              override: {
                configItemId: newItemId,
                phase: newItem.phase,
                enabled: true,
                customName: newItem.name,
                customDescription: newItem.description,
                customCategory: newItem.category,
                estimatedHours: newItem.estimatedHours,
                notes: `AI-suggested from SOW: ${newItem.reason}`,
              },
            }),
          }
        );
      }

      const totalApplied = selectedNewItems.length + selectedExistingItems.length;
      toast.success(`Applied ${totalApplied} configuration ${totalApplied === 1 ? 'item' : 'items'}`);
      onClose();
      if (onSOWCreated) {
        onSOWCreated(createdEngagementId ? { engagementId: createdEngagementId, companyId: selectedCompanyId } : undefined);
      }
    } catch (error: any) {
      console.error('Error applying suggestions:', error);
      toast.error('Failed to apply suggestions');
    } finally {
      setLoading(false);
    }
  };

  const toggleNewItem = (index: number) => {
    setSelectedNewItems(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const toggleExistingItem = (index: number) => {
    setSelectedExistingItems(prev =>
      prev.includes(index)
        ? prev.filter(i => i !== index)
        : [...prev, index]
    );
  };

  const toggleAllNewItems = () => {
    if (selectedNewItems.length === newItemSuggestions.length) {
      setSelectedNewItems([]);
    } else {
      setSelectedNewItems(newItemSuggestions.map((_, index) => index));
    }
  };

  const toggleAllExistingItems = () => {
    const enabledIndices = existingItemSuggestions
      .map((item, index) => (item.enabled ? index : -1))
      .filter(index => index !== -1);
    
    if (selectedExistingItems.length === enabledIndices.length) {
      setSelectedExistingItems([]);
    } else {
      setSelectedExistingItems(enabledIndices);
    }
  };

  const selectedCompany =
    isClientContext && selectedCompanyId === preselectedCompanyId
      ? { companyId: preselectedCompanyId!, companyName: preselectedCompanyName! }
      : companies.find(c => c.companyId === selectedCompanyId);

  // Helper function to get config item details from defaults
  const getConfigItemDetails = (itemId: string) => {
    const configItem = configDefaults.find(item => item.id === itemId);
    if (configItem) {
      return configItem;
    }
    
    // Fallback: extract a readable name from the ID if it's a custom/generated ID
    // IDs like "atlassian-config-1-1762484106928" should become "Atlassian Config 1"
    let fallbackName = itemId;
    if (itemId.includes('-')) {
      const parts = itemId.split('-');
      // Remove timestamp-looking numbers (long numeric strings at the end)
      const cleanedParts = parts.filter(part => !/^\d{10,}$/.test(part));
      // Capitalize and join
      fallbackName = normalizeAcronyms(cleanedParts
        .map(part => part.charAt(0).toUpperCase() + part.slice(1))
        .join(' '));
    }
    
    return { 
      id: itemId,
      name: fallbackName, 
      description: 'Configuration item details not available', 
      category: 'other' 
    };
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="size-5" />
            Add Statement of Work
          </DialogTitle>
          <DialogDescription>
            Upload the executed SOW document. AI will extract scope, HLOs, and configuration requirements from the file.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Company Selection */}
          <div className="space-y-2">
            <Label>Client Company *</Label>
            {isClientContext ? (
              // Opened from within a specific client (e.g. ClientDetailView):
              // the client is already known; show it read-only so users don't
              // have to re-pick and so we don't rely on /consultant/companies
              // returning a populated list.
              <div
                className="flex h-9 items-center rounded-md border border-input bg-muted/50 px-3 text-sm"
                aria-readonly="true"
                data-testid="add-sow-dialog-preselected-company"
              >
                {preselectedCompanyName}
              </div>
            ) : (
              <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a client..." />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(company => (
                    <SelectItem key={company.companyId} value={company.companyId}>
                      {company.companyName}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* SOW File Upload.
              Consultants attach SOWs as already-executed contract files
              (PDF/DOCX/TXT). The backend's upload-sow route extracts
              the text server-side and writes it onto the engagement so
              the analyzer + alignment engine see the same scope source. */}
          <div className="space-y-2">
            <Label htmlFor="sow-file">SOW Document *</Label>
            <input
              id="sow-file"
              type="file"
              accept=".pdf,.docx,.txt,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,text/plain"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                setSowFile(file);
              }}
              disabled={analyzing || analyzed || saving}
              data-testid="add-sow-dialog-file-input"
              className="block w-full text-sm text-muted-foreground file:mr-4 file:rounded-md file:border-0 file:bg-primary file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary-foreground hover:file:bg-primary/90"
            />
            <p className="text-sm text-muted-foreground">
              {sowFile ? (
                <>
                  Selected: <span className="font-medium">{sowFile.name}</span> (
                  {Math.round(sowFile.size / 1024)} KB)
                </>
              ) : (
                'Upload the executed SOW (PDF, DOCX, or TXT). The file will be parsed for scope and HLOs.'
              )}
            </p>
          </div>

          {/* Sales Discovery Notes (optional).
              Captured separately from the binding SOW scope so the LLM can
              use them as supplementary context (HLOs the BDM heard from
              the client during discovery) without confusing them with the
              executed contract. Surfaced on the alignment report so the CF
              Admin can see what sales heard vs. what scope covers. */}
          <div className="space-y-2">
            <Label htmlFor="sow-sales-notes">Sales Discovery Notes (optional)</Label>
            <Textarea
              id="sow-sales-notes"
              value={salesNotes}
              onChange={(e) => setSalesNotes(e.target.value)}
              placeholder="Paste discovery notes, business goals, or context the BDM captured during the sale. AI will use this as supplementary context (separate from the binding SOW scope)."
              rows={6}
              className="text-sm"
              disabled={analyzing || analyzed || saving}
              data-testid="add-sow-dialog-sales-notes"
            />
            <p className="text-sm text-muted-foreground">
              {salesNotes.length} characters
            </p>
          </div>

          {/* AI informational alert. Only shown pre-analysis, and only when
              the company has AI enabled -- when disabled, we deliberately
              don't mention AI at all, because the primary action will be a
              plain Save SOW. The actual analyze/save trigger lives in the
              footer so there is exactly one primary action at a time. */}
          {!analyzed && companyAiEnabled && (
            <Alert>
              <Sparkles className="h-4 w-4" />
              <AlertDescription>
                AI will parse the uploaded SOW, extract HLOs and scope, and recommend
                configuration items when you save.
              </AlertDescription>
            </Alert>
          )}

          {/* Analysis Results */}
          {analyzed && (
            <div className="space-y-4">
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-800">
                  <strong>Analysis Complete</strong> Review the suggestions below and apply them to configure {selectedCompany?.companyName}.
                </AlertDescription>
              </Alert>

              {/* New Configuration Items */}
              {newItemSuggestions.length > 0 && (
                <Card className="border-blue-200">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Sparkles className="size-4 text-blue-600" />
                        <CardTitle className="text-base flex items-center gap-2">
                          New Configuration Items Suggested
                          <Badge variant="secondary">{selectedNewItems.length}/{newItemSuggestions.length} selected</Badge>
                        </CardTitle>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={toggleAllNewItems}
                      >
                        {selectedNewItems.length === newItemSuggestions.length ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>
                    <CardDescription>
                      AI identified requirements in the SOW that aren't covered by existing configuration items
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {newItemSuggestions.map((item, index) => (
                      <Card key={index} className="bg-blue-50 border-blue-100">
                        <CardContent className="pt-4">
                          <div className="flex items-start gap-3 mb-2">
                            <Checkbox
                              checked={selectedNewItems.includes(index)}
                              onCheckedChange={() => toggleNewItem(index)}
                              className="mt-1"
                            />
                            <div className="flex-1">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <h4 className="text-sm font-semibold">{item.name}</h4>
                                  <p className="text-sm text-muted-foreground mt-1">{item.description}</p>
                                </div>
                                <div className="flex gap-2 ml-4">
                                  <Badge variant="outline">Phase {item.phase}</Badge>
                                  <Badge variant="outline">{item.category}</Badge>
                                </div>
                              </div>
                              <Alert className="mt-3 bg-white">
                                <AlertCircle className="h-4 w-4" />
                                <AlertDescription className="text-sm">
                                  <strong>Why:</strong> {item.reason}
                                </AlertDescription>
                              </Alert>
                              {item.estimatedHours > 0 && (
                                <p className="text-sm text-muted-foreground mt-2">
                                  Estimated: {item.estimatedHours} hours
                                </p>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </CardContent>
                </Card>
              )}

              {/* Existing Items Being Modified */}
              {existingItemSuggestions.filter(s => s.enabled).length > 0 && (
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <CheckCircle2 className="size-4 text-green-600" />
                        <CardTitle className="text-base flex items-center gap-2">
                          Existing Items to Enable
                          <Badge variant="secondary">{selectedExistingItems.length}/{existingItemSuggestions.filter(s => s.enabled).length} selected</Badge>
                        </CardTitle>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={toggleAllExistingItems}
                      >
                        {selectedExistingItems.length === existingItemSuggestions.filter(s => s.enabled).length ? 'Deselect All' : 'Select All'}
                      </Button>
                    </div>
                    <CardDescription>
                      These existing configuration items will be enabled and assigned to phases
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {existingItemSuggestions
                        .map((item, originalIndex) => ({ item, originalIndex }))
                        .filter(({ item }) => item.enabled)
                        .map(({ item, originalIndex }, displayIndex) => {
                          const configItem = getConfigItemDetails(item.itemId);
                          return (
                            <div key={displayIndex} className="flex items-start gap-3 p-3 border rounded hover:bg-muted/50 transition-colors">
                              <Checkbox
                                checked={selectedExistingItems.includes(originalIndex)}
                                onCheckedChange={() => toggleExistingItem(originalIndex)}
                                className="mt-1"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="flex items-start justify-between gap-2">
                                  <div className="flex-1">
                                    <h4 className="text-sm font-semibold">{configItem.name}</h4>
                                    {configItem.description && (
                                      <p className="text-sm text-muted-foreground mt-1">{configItem.description}</p>
                                    )}
                                  </div>
                                  <Badge variant="outline" className="shrink-0">Phase {item.phase}</Badge>
                                </div>
                                <div className="mt-2 text-sm text-muted-foreground">
                                  <strong>AI Recommendation:</strong> {item.reason}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={loading || saving || analyzing}
          >
            {analyzed ? 'Cancel' : 'Close'}
          </Button>
          {/*
            Primary pre-analyze action is mutually exclusive: exactly one of
            "Analyze & Save" or "Save SOW" is visible at a time, gated on the
            target company's AI preference (GET /companies/:id/preferences ->
            aiEnabled, default true). Both paths persist via POST /sows; the
            AI path additionally calls /config/analyze-sow after the save and
            surfaces suggestions for the user to apply.
          */}
          {!analyzed && companyAiEnabled && (
            <Button
              type="button"
              onClick={handleAnalyzeSOW}
              disabled={!sowFile || !selectedCompanyId || analyzing || saving}
              data-testid="add-sow-dialog-analyze-and-save"
            >
              {analyzing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Analyze & Save
                </>
              )}
            </Button>
          )}
          {!analyzed && !companyAiEnabled && (
            <Button
              type="button"
              onClick={handleSaveSOW}
              disabled={!sowFile || !selectedCompanyId || saving || analyzing}
              data-testid="add-sow-dialog-save"
            >
              {saving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Saving...
                </>
              ) : (
                <>Save SOW</>
              )}
            </Button>
          )}
          {analyzed && (
            <Button 
              onClick={handleApplySuggestions} 
              disabled={loading || (selectedNewItems.length === 0 && selectedExistingItems.length === 0)}
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Applying...
                </>
              ) : (
                <>
                  Apply Selected ({selectedNewItems.length + selectedExistingItems.length})
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}